import { NextRequest } from 'next/server';

import { utmParams } from '../constants/utm.constants';
import { UtmFromUrl } from '@bolttech/essentials';
import {
  ParamsFromUrlType,
  QuoteHashFromUrl,
  RedisSessionData,
  UTMParams,
  UTMParamsFromUrl,
} from '../types/utm.type';

/** Number of times the function will try to update REDIS **/
const MAX_RETRY_UPDATE_REDIS = 4;

/**
 * This function captures the UTM params from the url and creates an object
 * to be used for cookies and/or session.
 *
 * @param {NextRequest} req - Request provided by NextJS Middleware
 * @returns Returns an object that contains the UTM Params for cookies
 */
export const getUtmParamsFromUrl = (req: NextRequest): ParamsFromUrlType => {
  const url = req.nextUrl;
  const utmParamsFromUrlToCookie = {} as UTMParams;
  const utmParamsFromUrl = {} as UTMParamsFromUrl;

  utmParams.forEach((utmKey) => {
    if (utmKey === 'utm_source' && !url.searchParams.get(utmKey)) {
      utmParamsFromUrlToCookie[
        UtmFromUrl[utmKey as keyof typeof UtmFromUrl] as keyof UTMParams
      ] = utmParamsFromUrl[utmKey as keyof UTMParamsFromUrl] = 'Direct';
    } else {
      const utmValue = url.searchParams.get(utmKey);
      utmParamsFromUrlToCookie[
        UtmFromUrl[utmKey as keyof typeof UtmFromUrl] as keyof UTMParams
      ] = utmValue || null;
      // The utmParamsFromUrl cannot have null|undefined values
      if (utmValue)
        utmParamsFromUrl[utmKey as keyof UTMParamsFromUrl] = utmValue;
    }
  });

  return {
    utmParamsFromUrlToCookie,
    utmParamsFromUrl,
  };
};

/**
 *  Returns the H and X params from the URL
 * @param {NextRequest} req
 */
export const getQuoteHashFromUrl = (req: NextRequest): QuoteHashFromUrl => {
  const url = req.nextUrl;
  return {
    quoteHFromUrl: url.searchParams.get('h') || null,
    quoteXFromUrl: url.searchParams.get('x') || null,
  };
};

/**
 * Calls a rest api for update the REDIS session with the data captured by the url
 * @param {NextRequest} request
 * @param {string} originUrl
 * @param data
 * @param {number} currRetryNumber Current retry number
 */
export const updateRedisSession = async (
  request: NextRequest,
  originUrl: string,
  data: RedisSessionData,
  currRetryNumber = 1,
): Promise<Response | void> => {
  if (currRetryNumber >= MAX_RETRY_UPDATE_REDIS) {
    console.error('REDIS: Number of attempts exceeded.');
  } else {
    try {
      const responseApi = await fetch(`${originUrl}/api/session/redis`, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Cookie: request.headers.get('cookie') || '',
        },
        body: JSON.stringify(data),
      });
      const content = await responseApi.json();
      if (content.success) console.log('REDIS updated...');
      if (!content.success)
        console.error('Failed to update REDIS: ', content.error);
      return responseApi;
    } catch (e) {
      console.error('Failed to update REDIS[API ERROR]:', e);
      console.warn(`[${currRetryNumber + 1}]Retrying to update REDIS`);
      await updateRedisSession(request, originUrl, data, currRetryNumber + 1);
    }
  }
};
